import React, {useState, useEffect, useRef} from "react";
import {useSelector, useDispatch} from "react-redux";
import axios from "axios";
import {Row, Col, Card, Button, Spinner, Modal} from "react-bootstrap";
import moment from "moment";
import DateHelper from "../../../primer/DateHelper/DateHelper";
import DateBox from "../../../primer/DateBox";
import {getGreekCurrencyFormat, getDOYOptions} from "../../../../_helpers/commonFunctions";
import {fetchAccountingSettings} from "../../../../_apis/api";
import {toast} from "react-toastify";
import {
    setF4ReportRequestData,
    setF4TableBData,
    updateF4TableBRow,
    setF4TableCData,
    updateF4TableCRow,
    setF4LocalFields,
    setF4IsAmending,
    setF4ZoomLevel,
    setTaxisScrapingState
} from "../../../../_reducers/DataPanel/Accounting/accountingSlice";
import {useTranslation} from "../../../../hooks/useTranslation";

const EXTENSION_STORE_URL = "https://chromewebstore.google.com/detail/inimchhmmahoignojckagbdbjhigglce";

const GREEK_MONTHS = [
    "ΙΑΝΟΥΑΡΙΟΣ", "ΦΕΒΡΟΥΑΡΙΟΣ", "ΜΑΡΤΙΟΣ", "ΑΠΡΙΛΙΟΣ", "ΜΑΪΟΣ", "ΙΟΥΝΙΟΣ",
    "ΙΟΥΛΙΟΣ", "ΑΥΓΟΥΣΤΟΣ", "ΣΕΠΤΕΜΒΡΙΟΣ", "ΟΚΤΩΒΡΙΟΣ", "ΝΟΕΜΒΡΙΟΣ", "ΔΕΚΕΜΒΡΙΟΣ"
];

const EU_COUNTRIES = [
    { label: "ΑΥΣΤΡΙΑ", code: "AT" },
    { label: "ΒΕΛΓΙΟ", code: "BE" },
    { label: "ΒΟΥΛΓΑΡΙΑ", code: "BG" },
    { label: "Β. ΙΡΛΑΝΔΙΑ", code: "XI" },
    { label: "ΓΑΛΛΙΑ", code: "FR" },
    { label: "ΓΕΡΜΑΝΙΑ", code: "DE" },
    { label: "ΔΑΝΙΑ", code: "DK" },
    { label: "ΕΣΘΟΝΙΑ", code: "EE" },
    { label: "ΙΡΛΑΝΔΙΑ", code: "IE" },
    { label: "ΙΣΠΑΝΙΑ", code: "ES" },
    { label: "ΙΤΑΛΙΑ", code: "IT" },
    { label: "ΚΡΟΑΤΙΑ", code: "HR" },
    { label: "ΚΥΠΡΟΣ", code: "CY" },
    { label: "ΛΕΤΟΝΙΑ", code: "LV" },
    { label: "ΛΙΘΟΥΑΝΙΑ", code: "LT" },
    { label: "ΛΟΥΞΕΜΒΟΥΡΓΟ", code: "LU" },
    { label: "ΜΑΛΤΑ", code: "MT" },
    { label: "ΟΛΛΑΝΔΙΑ", code: "NL" },
    { label: "ΟΥΓΓΑΡΙΑ", code: "HU" },
    { label: "ΠΟΛΩΝΙΑ", code: "PL" },
    { label: "ΠΟΡΤΟΓΑΛΙΑ", code: "PT" },
    { label: "ΡΟΥΜΑΝΙΑ", code: "RO" },
    { label: "ΣΛΟΒΑΚΙΑ", code: "SK" },
    { label: "ΣΛΟΒΕΝΙΑ", code: "SI" },
    { label: "ΣΟΥΗΔΙΑ", code: "SE" },
    { label: "ΤΣΕΧΙΚΗ ΔΗΜΟΚΡΑΤΙΑ", code: "CZ" },
    { label: "ΦΙΝΛΑΝΔΙΑ", code: "FI" },
];

const emptyTableBRow = () => ({
    ctryDesc: "",
    bc4: "",
    bc5: "",
    bc6: "",
    bc7: "",
    bc8: ""
});

const emptyTableCRow = () => ({
    ctryDesc: "",
    cc4: "",
    cc5: "",
    cc6: "0",
    cc7: "0"
});

const DEFAULT_TAXIS_CREDENTIALS = {
    username: "",
    password: "",
    isAccountant: false,
    accountantAfm: ""
};

const checkExtensionInstalled = (timeoutMs = 800) => {
    return new Promise((resolve) => {
        const handleMessage = (event) => {
            if (event.source === window && event.data?.type === "TAXIS_EXTENSION_PONG") {
                window.removeEventListener("message", handleMessage);
                clearTimeout(timer);
                resolve(true);
            }
        };
        window.addEventListener("message", handleMessage);
        window.postMessage({type: "PING_TAXIS_EXTENSION"}, "*");
        const timer = setTimeout(() => {
            window.removeEventListener("message", handleMessage);
            resolve(false);
        }, timeoutMs);
    });
};

const isChromiumBrowser = () => {
    const nav = window.navigator;
    if (nav.userAgentData && Array.isArray(nav.userAgentData.brands)) {
        return nav.userAgentData.brands.some((b) =>
            ["Chromium", "Google Chrome", "Microsoft Edge", "Opera", "Brave"].includes(b.brand)
        );
    }
    const ua = nav.userAgent;
    return /Chrome|Chromium|Edg|OPR/i.test(ua) && !(/Firefox|FxiOS|Safari/i.test(ua) && !/Chrome|Edg|OPR/i.test(ua));
};

const AccountingIssueF4VatInformation = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const company = useSelector((state) => state.COMPANY_DATA.company);
    const companyDetails = useSelector((state) => state.COMPANY_DATA.companyDetails);
    const taxisCredentials = useSelector((state) => state.ACCOUNTING_DATA?.taxisCredentials) || DEFAULT_TAXIS_CREDENTIALS;
    const requestData = useSelector((state) => state.ACCOUNTING_DATA.f4ReportRequestData);
    const tableBData = useSelector((state) => state.ACCOUNTING_DATA.f4TableBData) || Array.from({length: 14}, emptyTableBRow);
    const tableCData = useSelector((state) => state.ACCOUNTING_DATA.f4TableCData) || Array.from({length: 5}, emptyTableCRow);
    const localFields = useSelector((state) => state.ACCOUNTING_DATA.f4LocalFields) || {};
    const isAmending = useSelector((state) => state.ACCOUNTING_DATA.f4IsAmending) || false;
    const zoomLevel = useSelector((state) => state.ACCOUNTING_DATA.f4ZoomLevel) || 1;

    const [taxisLoading, setTaxisLoading] = useState(false);
    const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
    const [showExtensionModal, setShowExtensionModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragScrollLeft, setDragScrollLeft] = useState(0);

    const activeTaxisCompanyIdRef = useRef(null);
    const prevCompanyIdRef = useRef(company?.id);
    const hasDataInRedux = tableBData.some(r => (r.bc4 && r.bc4.trim() !== "") || (r.ctryDesc && r.ctryDesc.trim() !== ""));

    const mFrom = moment(requestData?.date_from, "DD/MM/YYYY");
    const mTo = moment(requestData?.date_to, "DD/MM/YYYY");
    const selectedYear = mFrom.isValid() ? mFrom.format("YYYY") : moment().format("YYYY");
    const selectedYearShort = mFrom.isValid() ? mFrom.format("YY") : moment().format("YY");
    const selectedMonthIndex = mFrom.isValid() ? mFrom.month() : moment().month();
    const selectedMonthGreek = GREEK_MONTHS[selectedMonthIndex] || "";
    const selectedQuarter = mFrom.isValid() ? Math.ceil((mFrom.month() + 1) / 3) : 1;
    const todayStr = moment().format("DDMMYYYY");

    const doyOptions = getDOYOptions();
    let doyLabel = companyDetails?.doy || "";
    let doyCode = "";
    if (companyDetails?.doy) {
        const matchedDoy = doyOptions.find(opt =>
            String(opt.value) === String(companyDetails.doy) ||
            String(opt.label) === String(companyDetails.doy)
        );
        if (matchedDoy) {
            doyLabel = matchedDoy.label;
            doyCode = String(matchedDoy.value).padStart(4, "0");
        }
    }

    const handleLocalFieldChange = (e) => {
        dispatch(setF4LocalFields({[e.target.name]: e.target.value}));
    };

    const handleTableBChange = (rowIndex, field, value) => {
        dispatch(updateF4TableBRow({rowIndex, field, value}));
    };

    const handleTableCChange = (rowIndex, field, value) => {
        dispatch(updateF4TableCRow({rowIndex, field, value}));
    };

    const sumBc5 = tableBData.reduce((acc, r) => acc + (parseFloat(String(r.bc5).replace(",", ".")) || 0), 0);
    const sumBc6 = tableBData.reduce((acc, r) => acc + (parseFloat(String(r.bc6).replace(",", ".")) || 0), 0);
    const sumBc7 = tableBData.reduce((acc, r) => acc + (parseFloat(String(r.bc7).replace(",", ".")) || 0), 0);
    const sumBc8 = tableBData.reduce((acc, r) => acc + (parseFloat(String(r.bc8).replace(",", ".")) || 0), 0);

    useEffect(() => {
        if (!company?.id) return;
        dispatch(fetchAccountingSettings({company: company.id}));
    }, [company?.id, dispatch]);

    useEffect(() => {
        const checkExtension = (event) => {
            if (event.source !== window) return;
            if (event.data?.type === "TAXIS_EXTENSION_READY" || event.data?.type === "TAXIS_EXTENSION_PONG") {
                setIsExtensionInstalled(true);
            }
        };
        window.addEventListener("message", checkExtension);
        window.postMessage({type: "PING_TAXIS_EXTENSION"}, "*");
        return () => window.removeEventListener("message", checkExtension);
    }, []);

    useEffect(() => {
        const handleExtensionMessages = (event) => {
            if (event.source !== window || !event.data) return;
            const {type, action} = event.data;
            const success = event.data.success ?? event.data.response?.success;
            const rawError = event.data.error || event.data.response?.error;

            if (type === "TO_ERP_AUTOFILL_VAT_RESPONSE") {
                if (success === false || rawError) {
                    setTaxisLoading(false);
                    activeTaxisCompanyIdRef.current = null;
                    toast.error(rawError || "Αποτυχία εκκίνησης του Taxis Bridge.", {autoClose: 10000});
                }
                return;
            }

            if (action === "AUTOFILL_COMPLETED" || type === "TAXIS_AUTOFILL_COMPLETED") {
                setTaxisLoading(false);
                activeTaxisCompanyIdRef.current = null;
                const count = event.data.filledCount ?? event.data.response?.filledCount ?? 0;

                toast.success(
                    `Ο πίνακας συμπληρώθηκε επιτυχώς (${count} ${count === 1 ? 'εγγραφή' : 'εγγραφές'}). Ελέγξτε και υποβάλετε στο Taxisnet!`,
                    {
                        autoClose: 7000,
                        pauseOnHover: true
                    }
                );
                return;
            }

            if (type === "TO_ERP_POST_FILL_SCRAPING_STARTED") {
                toast.info(event.data.message || "Έλεγχος υποβολής και αυτόματη άντληση δήλωσης Φ4 από το Taxisnet...", {
                    autoClose: 6000,
                    pauseOnHover: true
                });
                return;
            }

            if (type === "TO_ERP_POST_FILL_VERIFICATION_RESULT") {
                if (event.data.found) {
                    toast.success(event.data.message || "Η δήλωση Φ4 εντοπίστηκε υποβληθείσα και αντλήθηκε επιτυχώς!", {
                        autoClose: 7000,
                        pauseOnHover: true
                    });
                } else {
                    toast.info(event.data.message || "Η δήλωση Φ4 δεν έχει υποβληθεί ακόμη στο Taxisnet.", {
                        autoClose: 7000,
                        pauseOnHover: true
                    });
                }
                return;
            }

            if (action === "AUTOFILL_FAILED" || action === "AUTOFILL_FAILED_AND_CLOSE") {
                setTaxisLoading(false);
                activeTaxisCompanyIdRef.current = null;
                dispatch(setTaxisScrapingState({active: false, message: ""}));
                toast.error(rawError || "Η διαδικασία στο Taxisnet απέτυχε.", {autoClose: 10000, pauseOnHover: true});
            }
        };

        window.addEventListener("message", handleExtensionMessages);
        return () => window.removeEventListener("message", handleExtensionMessages);
    }, [dispatch]);

    useEffect(() => {
        if (!company?.id) return;
        if (prevCompanyIdRef.current && prevCompanyIdRef.current !== company.id) {
            dispatch(setF4TableBData(Array.from({length: 14}, emptyTableBRow)));
            dispatch(setF4TableCData(Array.from({length: 5}, emptyTableCRow)));
            dispatch(setF4IsAmending(false));
        }

        prevCompanyIdRef.current = company.id;
        dispatch(fetchAccountingSettings({company: company.id}));
    }, [company?.id, dispatch]);

    const fetchF4Data = () => {
        if (!requestData?.date_from || !requestData?.date_to) {
            toast.warning("Παρακαλώ επιλέξτε εύρος ημερομηνιών.");
            return;
        }

        setLoading(true);
        axios.post(`${process.env.REACT_APP_API_URL2}/accounting/calculate-f4-doc`, {
            company: company?.id,
            date_from: requestData.date_from,
            date_to: requestData.date_to
        }).then((res) => {
            setLoading(false);
            if (res.data?.status === "200") {
                const incomingRows = res.data.rows || [];

                if (incomingRows.length === 0) {
                    dispatch(setF4TableBData(Array.from({length: 14}, emptyTableBRow)));
                    toast.info("Δεν βρέθηκαν δεδομένα.");
                } else {
                    const incoming = incomingRows.slice(0, 14);
                    while (incoming.length < 14) incoming.push(emptyTableBRow());
                    dispatch(setF4TableBData(incoming));
                    toast.success(`Αντλήθηκαν επιτυχώς ${incomingRows.length} ${incomingRows.length === 1 ? 'εγγραφή' : 'εγγραφές'}.`);
                }
            } else {
                dispatch(setF4TableBData(Array.from({length: 14}, emptyTableBRow)));
                toast.error(res.data?.message || "Σφάλμα κατά τον υπολογισμό.");
            }
        }).catch((err) => {
            setLoading(false);
            dispatch(setF4TableBData(Array.from({length: 14}, emptyTableBRow)));
            console.error(err);
            toast.error("Σφάλμα κατά την άντληση στοιχείων Φ4.");
        });
    };

    const handleTriggerExtension = async () => {
        if (!isChromiumBrowser()) {
            toast.warning("Απαιτείται περιηγητής Chromium (Chrome, Edge, Brave, Opera).", {autoClose: 6000});
            return;
        }

        let installed = isExtensionInstalled;
        if (!installed) {
            installed = await checkExtensionInstalled(800);
            if (installed) setIsExtensionInstalled(true);
        }

        if (!installed) {
            setShowExtensionModal(true);
            return;
        }

        if (!taxisCredentials?.username || !taxisCredentials?.password) {
            toast.error("Δεν έχουν οριστεί κωδικοί Taxisnet στις ρυθμίσεις.");
            return;
        }

        const resolvedAfm = company?.vatNumber?.trim();
        if (!resolvedAfm) {
            toast.error("Δεν βρέθηκε έγκυρο ΑΦΜ εταιρείας.");
            return;
        }

        const validBRows = tableBData.filter(r => r.ctryDesc && r.bc4 && r.bc4.trim() !== "");
        const validCRows = tableCData.filter(r => r.ctryDesc && r.cc4 && r.cc4.trim() !== "");

        if (validBRows.length === 0 && validCRows.length === 0) {
            toast.warning("Δεν υπάρχουν συμπληρωμένες γραμμές με ΑΦΜ προς καταχώριση στον πίνακα Φ4.");
            return;
        }

        try {
            const targetMonth = mFrom.isValid() ? mFrom.month() + 1 : 1;
            const payload = {
                credentials: {
                    username: taxisCredentials.username,
                    password: taxisCredentials.password,
                    isAccountant: Boolean(taxisCredentials.isAccountant),
                    accountantAfm: taxisCredentials.accountantAfm
                },
                year: String(selectedYear),
                companyAfm: resolvedAfm,
                period: {
                    dateFrom: requestData.date_from,
                    dateTo: requestData.date_to,
                    month: targetMonth,
                    year: Number(selectedYear)
                },
                actionType: "FILL_F4",
                f4Data: {
                    isAmending: isAmending,
                    tableBData: validBRows,
                    tableCData: validCRows
                }
            };

            activeTaxisCompanyIdRef.current = company?.id;
            setTaxisLoading(true);

            toast.info(`Έναρξη διαδικασίας. Συμπλήρωση Φ4 για ${selectedMonthGreek} ${selectedYear}...`, {autoClose: 4000});
            window.postMessage({type: "FROM_ERP_AUTOFILL_VAT", payload}, "*");
        } catch (err) {
            console.error("[ERP F4 Taxis Error]:", err);
            setTaxisLoading(false);
            activeTaxisCompanyIdRef.current = null;
            toast.error("Σφάλμα κατά την προετοιμασία της αυτόματης συμπλήρωσης.");
        }
    };

    const DateCombSlash = ({ value = "", color = "var(--f4-color)" }) => {
        let d = "  ", m = "  ", y = "  ";

        if (value) {
            const str = String(value).trim();
            if (str.includes("/")) {
                const parts = str.split("/");
                d = (parts[0] || "").padStart(2, "0").slice(-2);
                m = (parts[1] || "").padStart(2, "0").slice(-2);
                y = (parts[2] || "").slice(-2).padStart(2, "0");
            } else {
                const digits = str.replace(/\D/g, "");
                if (digits.length >= 6) {
                    d = digits.slice(0, 2);
                    m = digits.slice(2, 4);
                    y = digits.slice(-2);
                }
            }
        }

        return (
            <div style={{ display: "flex", width: "100%", height: "100%", alignItems: "flex-end" }}>
                <div style={{ flex: 2, display: "flex", height: "100%" }}>
                    <StrictComb length={2} value={d} />
                </div>
                <div style={{
                    width: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.85rem",
                    fontWeight: "bold",
                    color: color,
                    paddingBottom: "2px"
                }}>/</div>
                <div style={{ flex: 2, display: "flex", height: "100%" }}>
                    <StrictComb length={2} value={m} />
                </div>
                <div style={{
                    width: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.85rem",
                    fontWeight: "bold",
                    color: color,
                    paddingBottom: "2px"
                }}>/</div>
                <div style={{ flex: 2, display: "flex", height: "100%" }}>
                    <StrictComb length={2} value={y} />
                </div>
            </div>
        );
    };

    const StrictComb = ({length, value = "", onChange, label = null}) => {
        const chars = String(value || "").padEnd(length, " ").slice(0, length).split("");

        return (
            <div
                style={{display: "flex", flexDirection: "column", height: "100%", width: "100%", position: "relative"}}>
                {onChange && (
                    <input
                        type="text"
                        value={String(value || "").replace(/\s/g, "")}
                        onChange={(e) => onChange(e.target.value.slice(0, length))}
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            opacity: 0,
                            zIndex: 10,
                            cursor: "text"
                        }}
                    />
                )}
                {label && (
                    <div style={{
                        fontSize: "0.55rem",
                        fontWeight: "bold",
                        textAlign: "center",
                        color: "var(--f4-color)",
                        lineHeight: 1.2,
                        paddingTop: "2px"
                    }}>
                        {label}
                    </div>
                )}
                <div style={{display: "flex", flexGrow: 1}}>
                    {chars.map((char, idx) => (
                        <div key={idx} style={{
                            flex: 1,
                            position: "relative",
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "center"
                        }}>
                            <span style={{fontSize: "0.8rem", fontWeight: "bold", color: "#000", paddingBottom: "2px"}}>
                                {char.trim()}
                            </span>
                            {idx < length - 1 && (
                                <div style={{
                                    position: "absolute",
                                    right: 0,
                                    bottom: 0,
                                    width: "1px",
                                    height: "5px",
                                    backgroundColor: "var(--f4-color)"
                                }}/>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="w-100 d-flex flex-column" style={{minHeight: "100%"}}>
            <style>{`
                :root {
                    --f4-color: #D2825A;
                    --f4-box: #F9D38D;
                }
                .f4-document {
                    font-family: Arial, Helvetica, sans-serif;
                    color: var(--f4-color);
                    border: 2px solid var(--f4-color);
                    box-sizing: border-box;
                    background-color: #fff;
                    width: 1060px;
                    margin: 0 auto;
                    position: relative;
                }
                .f4-document * {
                    box-sizing: border-box;
                }
                
                .f4-code-box {
                    width: 40px;
                    min-width: 40px;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    font-size: 0.8rem;
                    color: var(--f4-color);
                    background-color: #fff;
                    border-right: 1px solid var(--f4-color);
                    flex-shrink: 0;
                }
                .f4-code-box.yellow {
                    background-color: var(--f4-box);
                }

                .f4-input-clean {
                    width: 100%;
                    height: 100%;
                    border: none;
                    background: transparent;
                    font-size: 0.75rem;
                    font-weight: bold;
                    color: #000;
                    outline: none;
                }
                .f4-input-clean:focus {
                    background-color: #FFF9F5;
                }

                .f4-row {
                    display: flex;
                    width: 100%;
                    border-bottom: 1px solid var(--f4-color);
                }
                
                .f4-custom-checkbox {
                    -webkit-appearance: none;
                    appearance: none;
                    margin: 0;
                    width: 16px;
                    height: 16px;
                    border: 1px solid var(--f4-color);
                    background-color: #fff;
                    cursor: pointer;
                    display: inline-grid;
                    place-content: center;
                    outline: none;
                }
                .f4-custom-checkbox:checked::before {
                    content: "";
                    width: 5px;
                    height: 10px;
                    border: solid #000;
                    border-width: 0 2px 2px 0;
                    transform: rotate(45deg);
                    margin-bottom: 2px;
                }
                .f4-dotted-line {
                    border-bottom: 1.5px dotted var(--f4-color);
                    height: 16px;
                    margin: 0 4px;
                    flex-grow: 1;
                }
                .vertical-text {
                    writing-mode: vertical-rl;
                    transform: rotate(180deg);
                    text-align: center;
                    padding: 4px;
                    margin: 0 auto;
                }
                .f4-comb-input {
                    width: 100%;
                    height: 25px;
                    border: none;
                    outline: none;
                    background-color: transparent;
                    font-family: monospace, "Courier New", Courier;
                    font-size: 0.85rem;
                    font-weight: bold;
                    color: #000;
                    text-align: center;
                    letter-spacing: 4px;
                    padding: 0 4px;
                    background-image: linear-gradient(to right, transparent calc(100% - 1px), var(--f4-color) calc(100% - 1px));
                    background-size: calc(100% / 12) 6px;
                    background-repeat: repeat-x;
                    background-position: bottom;
                }
                .f4-comb-input:focus {
                    background-color: #FFF9F5;
                }
            `}</style>

            <Card className="mb-3 border-0 shadow-sm print-hide w-100">
                <Card.Body className="p-3">
                    <Row className="align-items-center">
                        <Col md={2}>
                            <DateHelper
                                dateFromAndToFunction={(from, to) => dispatch(setF4ReportRequestData({
                                    date_from: from,
                                    date_to: to
                                }))}
                            />
                        </Col>
                        <Col md={2}>
                            <DateBox
                                name="date_from"
                                selected={mFrom.isValid() ? mFrom.toDate() : null}
                                label={t("SalesMyData.dateFrom")}
                                onChange={(e) => dispatch(setF4ReportRequestData({
                                    date_from: moment(e).format("DD/MM/YYYY")
                                }))}
                                allowOtherYears={false}
                            />
                        </Col>
                        <Col md={2}>
                            <DateBox
                                name="date_to"
                                selected={mTo.isValid() ? mTo.toDate() : null}
                                label={t("SalesMyData.dateTo")}
                                onChange={(e) => dispatch(setF4ReportRequestData({
                                    date_to: moment(e).format("DD/MM/YYYY")
                                }))}
                                allowOtherYears={false}
                            />
                        </Col>
                        <Col md={6}>
                            <Button onClick={fetchF4Data} disabled={loading} className="mt-4" variant="primary">
                                Υπολογισμός Φ4 {loading && <Spinner className="ml-2" animation="border" size="sm"/>}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {hasDataInRedux && (
                <>
                    <div
                        className="w-100 py-3"
                        style={{
                            overflowX: "auto",
                            overflowY: "hidden",
                            cursor: zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "auto"
                        }}
                        onMouseDown={(e) => {
                            if (zoomLevel <= 1) return;
                            setIsDragging(true);
                            setDragStartX(e.clientX);
                            setDragScrollLeft(e.currentTarget.scrollLeft);
                        }}
                        onMouseLeave={() => setIsDragging(false)}
                        onMouseUp={() => setIsDragging(false)}
                        onMouseMove={(e) => {
                            if (!isDragging) return;
                            e.preventDefault();
                            const walk = (e.clientX - dragStartX) * 1.5;
                            e.currentTarget.scrollLeft = dragScrollLeft - walk;
                        }}
                    >
                        <div style={{width: "1060px", margin: "0 auto", userSelect: isDragging ? "none" : "auto"}}>
                            <div className="f4-document shadow-sm" style={{zoom: zoomLevel}}>
                                <div className="position-relative w-100"
                                     style={{height: "185px", borderBottom: "2px solid var(--f4-color)"}}>
                                    <div style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "145px",
                                        height: "145px",
                                        pointerEvents: "none"
                                    }}>
                                        <svg viewBox="0 0 145 145" width="100%" height="100%">
                                            <defs>
                                                <clipPath id="f4-triangle-clip">
                                                    <polygon points="0,0 145,0 0,145"/>
                                                </clipPath>
                                            </defs>

                                            <g clipPath="url(#f4-triangle-clip)">
                                                <polygon points="0,0 145,0 0,145" fill="var(--f4-color)"/>

                                                <text x="12" y="43" fill="#fff" fontSize="42"
                                                      fontFamily="'Times New Roman', Times, serif" fontWeight="normal">
                                                    Φ
                                                </text>
                                                <text x="40" y="46" fill="#fff" fontSize="27"
                                                      fontFamily="'Times New Roman', Times, serif" fontWeight="bold">
                                                    4
                                                </text>

                                                <g transform="translate(64, 72) rotate(-45)">
                                                    <line x1="-85" y1="-12" x2="85" y2="-12" stroke="#fff"
                                                          strokeWidth="1.3"
                                                          opacity="0.95"/>
                                                    <text
                                                        x="0"
                                                        y="4.5"
                                                        textAnchor="middle"
                                                        fill="#fff"
                                                        fontSize="16"
                                                        fontWeight="900"
                                                        fontFamily="Arial, sans-serif"
                                                        letterSpacing="9"
                                                    >
                                                        TAXIS
                                                    </text>
                                                    <line x1="-85" y1="12" x2="85" y2="12" stroke="#fff"
                                                          strokeWidth="1.3"
                                                          opacity="0.95"/>
                                                </g>
                                            </g>
                                        </svg>
                                    </div>

                                    <div style={{position: "absolute", left: "220px", top: "15px", width: "450px"}}>
                                        <div style={{
                                            fontSize: "1.1rem",
                                            fontWeight: "bold",
                                            color: "var(--f4-color)",
                                            letterSpacing: "0.5px"
                                        }}>
                                            ΑΝΑΚΕΦΑΛΑΙΩΤΙΚΟΣ ΠΙΝΑΚΑΣ
                                        </div>
                                        <div style={{
                                            fontSize: "0.8rem",
                                            fontWeight: "bold",
                                            color: "var(--f4-color)",
                                            marginTop: "4px",
                                            lineHeight: "1.3"
                                        }}>
                                            ΕΝΔΟΚΟΙΝΟΤΙΚΩΝ ΠΑΡΑΔΟΣΕΩΝ ΑΓΑΘΩΝ ΚΑΙ ΠΑΡΟΧΩΝ ΥΠΗΡΕΣΙΩΝ
                                            <br/>
                                            ΑΠΟΘΕΜΑΤΩΝ ΣΤΗ ΔΙΑΘΕΣΗ ΣΥΓΚΕΚΡΙΜΕΝΟΥ ΠΕΛΑΤΗ ΣΕ ΑΛΛΟ Κ-Μ
                                        </div>
                                        <div style={{
                                            fontSize: "0.7rem",
                                            color: "var(--f4-color)",
                                            marginTop: "8px",
                                            textAlign: "center",
                                            width: "360px"
                                        }}>
                                            Υποβάλλεται σε δύο (2) αντίτυπα (έντυπη υποβολή)
                                        </div>
                                    </div>

                                    <div style={{position: "absolute", right: "12px", top: "15px", width: "320px"}}>
                                        <div style={{display: "flex", alignItems: "flex-end", marginBottom: "8px"}}>
                                            <div style={{
                                                fontSize: "0.55rem",
                                                fontWeight: "bold",
                                                color: "var(--f4-color)",
                                                textAlign: "right",
                                                width: "100px",
                                                paddingRight: "8px",
                                                lineHeight: "1.1"
                                            }}>
                                                ΑΡΙΘΜΟΣ<br/>ΔΗΛΩΣΗΣ
                                            </div>
                                            <div style={{
                                                display: "flex",
                                                border: "1px solid var(--f4-color)",
                                                height: "32px",
                                                flexGrow: 1,
                                                position: "relative"
                                            }}>
                                                <div className="f4-code-box yellow"
                                                     style={{width: "34px", minWidth: "34px"}}>
                                                    003
                                                </div>
                                                <div style={{flex: 2.2, borderRight: "1px solid var(--f4-color)"}}>
                                                    <StrictComb
                                                        length={6}
                                                        value={localFields.f003_num}
                                                        onChange={(v) => handleLocalFieldChange({
                                                            target: {
                                                                name: "f003_num",
                                                                value: v
                                                            }
                                                        })}
                                                        label="ΑΡΙΘΜΟΣ"
                                                    />
                                                </div>
                                                <div style={{flex: 1}}>
                                                    <StrictComb length={2} value={selectedYearShort} label="ΕΤΟΣ"/>
                                                </div>
                                                <span style={{
                                                    position: "absolute",
                                                    right: "-8px",
                                                    top: "-4px",
                                                    color: "var(--f4-color)",
                                                    fontSize: "0.75rem",
                                                    fontWeight: "bold"
                                                }}>*</span>
                                            </div>
                                        </div>

                                        <div style={{display: "flex", alignItems: "flex-end"}}>
                                            <div style={{
                                                fontSize: "0.55rem",
                                                fontWeight: "bold",
                                                color: "var(--f4-color)",
                                                textAlign: "right",
                                                width: "100px",
                                                paddingRight: "8px",
                                                lineHeight: "1.1"
                                            }}>
                                                ΗΜΕΡΟΜΗΝΙΑ<br/>ΥΠΟΒΟΛΗΣ ΔΗΛΩΣΗΣ
                                            </div>
                                            <div style={{
                                                display: "flex",
                                                border: "1px solid var(--f4-color)",
                                                height: "24px",
                                                flexGrow: 1,
                                                position: "relative"
                                            }}>
                                                <div className="f4-code-box"
                                                     style={{width: "34px", minWidth: "34px"}}>004
                                                </div>
                                                <DateCombSlash value={todayStr} />
                                                <span style={{
                                                    position: "absolute",
                                                    right: "-8px",
                                                    top: "-4px",
                                                    color: "var(--f4-color)",
                                                    fontSize: "0.75rem",
                                                    fontWeight: "bold"
                                                }}>*</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{
                                        position: "absolute",
                                        left: "130px",
                                        top: "115px",
                                        display: "flex",
                                        alignItems: "flex-end",
                                        width: "480px"
                                    }}>
                                        <div style={{
                                            fontSize: "0.8rem",
                                            fontWeight: "bold",
                                            color: "var(--f4-color)",
                                            paddingBottom: "2px"
                                        }}>Δ.Ο.Υ.
                                        </div>
                                        <div className="f4-dotted-line d-flex align-items-end">
                                            <span style={{
                                                fontSize: "0.75rem",
                                                fontWeight: "bold",
                                                color: "#000",
                                                paddingLeft: "8px"
                                            }}>{doyLabel}</span>
                                        </div>
                                        <div style={{
                                            display: "flex",
                                            border: "1px solid var(--f4-color)",
                                            height: "24px",
                                            width: "135px",
                                            position: "relative",
                                            marginLeft: "6px"
                                        }}>
                                            <div className="f4-code-box" style={{width: "38px", minWidth: "38px"}}>001
                                            </div>
                                            <StrictComb length={4} value={doyCode}/>
                                            <span style={{
                                                position: "absolute",
                                                right: "-8px",
                                                top: "-4px",
                                                color: "var(--f4-color)",
                                                fontSize: "0.75rem",
                                                fontWeight: "bold"
                                            }}>*</span>
                                        </div>
                                    </div>

                                    <div style={{
                                        position: "absolute",
                                        bottom: 0,
                                        left: 0,
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "flex-end"
                                    }}>
                                        <div style={{width: "420px"}}>
                                            <div style={{
                                                fontSize: "0.7rem",
                                                fontWeight: "bold",
                                                color: "var(--f4-color)",
                                                textAlign: "center",
                                                marginBottom: "2px"
                                            }}>
                                                Ημερολογιακή περίοδος
                                            </div>
                                            <div style={{
                                                display: "flex",
                                                borderTop: "1px solid var(--f4-color)",
                                                borderRight: "1px solid var(--f4-color)",
                                                height: "28px"
                                            }}>
                                                <div style={{
                                                    padding: "0 6px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    fontSize: "0.75rem",
                                                    fontWeight: "bold",
                                                    color: "var(--f4-color)"
                                                }}>από
                                                </div>
                                                <div className="f4-code-box yellow" style={{
                                                    width: "36px",
                                                    minWidth: "36px",
                                                    borderLeft: "1px solid var(--f4-color)"
                                                }}>006
                                                </div>
                                                <div style={{flex: 1}}>
                                                    <DateCombSlash value={requestData?.date_from || ""} />
                                                </div>
                                                <div style={{
                                                    padding: "0 6px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    fontSize: "0.75rem",
                                                    fontWeight: "bold",
                                                    color: "var(--f4-color)",
                                                    borderLeft: "1px solid var(--f4-color)",
                                                    borderRight: "1px solid var(--f4-color)"
                                                }}>έως
                                                </div>
                                                <div style={{flex: 1}}>
                                                    <DateCombSlash value={requestData?.date_to || ""} />
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{
                                            display: "flex",
                                            borderTop: "1px solid var(--f4-color)",
                                            borderRight: "1px solid var(--f4-color)",
                                            height: "42px",
                                            marginLeft: "15px"
                                        }}>
                                            <div className="f4-code-box yellow" style={{
                                                borderLeft: "1px solid var(--f4-color)",
                                                width: "38px",
                                                minWidth: "38px"
                                            }}>007
                                            </div>
                                            <div style={{width: "48px", display: "flex", flexDirection: "column"}}>
                                                <div style={{
                                                    fontSize: "0.45rem",
                                                    fontWeight: "bold",
                                                    color: "var(--f4-color)",
                                                    textAlign: "center",
                                                    borderBottom: "1px solid var(--f4-color)",
                                                    lineHeight: "1.1",
                                                    padding: "2px 0"
                                                }}>
                                                    ΔΙΟΡΘΩ-<br/>ΤΙΚΟΣ
                                                </div>
                                                <div style={{
                                                    display: "flex",
                                                    flexGrow: 1,
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        className="f4-custom-checkbox"
                                                        checked={isAmending}
                                                        onChange={(e) => dispatch(setF4IsAmending(e.target.checked))}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{width: "210px", marginLeft: "15px"}}>
                                            <div style={{
                                                fontSize: "0.7rem",
                                                fontWeight: "bold",
                                                color: "var(--f4-color)",
                                                textAlign: "center",
                                                marginBottom: "2px"
                                            }}>
                                                Μήνας
                                            </div>
                                            <div style={{
                                                display: "flex",
                                                borderTop: "1px solid var(--f4-color)",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderLeft: "1px solid var(--f4-color)",
                                                height: "28px"
                                            }}>
                                                <div className="f4-code-box yellow"
                                                     style={{width: "38px", minWidth: "38px"}}>
                                                    009
                                                </div>
                                                <div style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexGrow: 1,
                                                    fontWeight: "bold",
                                                    fontSize: "0.75rem",
                                                    color: "#000"
                                                }}>
                                                    {selectedMonthGreek}
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{flexGrow: 1, marginLeft: "15px"}}>
                                            <div style={{
                                                fontSize: "0.7rem",
                                                fontWeight: "bold",
                                                color: "var(--f4-color)",
                                                textAlign: "center",
                                                marginBottom: "2px"
                                            }}>
                                                Τρίμηνο / έτος
                                            </div>
                                            <div style={{
                                                display: "flex",
                                                borderTop: "1px solid var(--f4-color)",
                                                borderLeft: "1px solid var(--f4-color)",
                                                height: "28px"
                                            }}>
                                                <div className="f4-code-box yellow"
                                                     style={{width: "38px", minWidth: "38px"}}>
                                                    008
                                                </div>
                                                <div style={{width: "35px"}}>
                                                    <StrictComb length={1} value={String(selectedQuarter)}/>
                                                </div>
                                                <div style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "24px",
                                                    fontSize: "1.2rem",
                                                    color: "var(--f4-color)",
                                                    borderLeft: "1px solid var(--f4-color)",
                                                    borderRight: "1px solid var(--f4-color)"
                                                }}>
                                                    /
                                                </div>
                                                <div style={{flexGrow: 1}}>
                                                    <StrictComb length={4} value={selectedYear}/>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="w-100" style={{borderBottom: "2px solid var(--f4-color)"}}>
                                    <div style={{
                                        padding: "4px 8px",
                                        fontWeight: "bold",
                                        fontSize: "0.8rem",
                                        color: "var(--f4-color)",
                                        borderBottom: "1px solid var(--f4-color)"
                                    }}>
                                        Α. ΠΙΝΑΚΑΣ ΜΕ ΤΑ ΣΤΟΙΧΕΙΑ ΤΟΥ ΔΗΛΟΥΝΤΟΣ
                                    </div>
                                    <div className="f4-row" style={{height: "30px"}}>
                                        <div className="f4-code-box">011</div>
                                        <div style={{
                                            width: "160px",
                                            padding: "0 8px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontWeight: "bold",
                                            fontSize: "0.65rem",
                                            color: "var(--f4-color)",
                                            borderRight: "1px solid var(--f4-color)"
                                        }}>
                                            ΕΠΩΝΥΜΟ 'Η ΕΠΩΝΥΜΙΑ
                                        </div>
                                        <div style={{
                                            flexGrow: 1,
                                            padding: "0 8px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontWeight: "bold",
                                            fontSize: "0.75rem",
                                            color: "#000"
                                        }}>
                                            {company?.name || ""}
                                        </div>
                                    </div>
                                    <div className="f4-row" style={{height: "32px"}}>
                                        <div className="f4-code-box">012</div>
                                        <div style={{
                                            width: "70px",
                                            padding: "0 6px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontWeight: "bold",
                                            fontSize: "0.65rem",
                                            color: "var(--f4-color)",
                                            borderRight: "1px solid var(--f4-color)"
                                        }}>
                                            ΟΝΟΜΑ
                                        </div>
                                        <div style={{
                                            width: "280px",
                                            borderRight: "1px solid var(--f4-color)",
                                            padding: "0 6px"
                                        }}>
                                            <input
                                                type="text"
                                                name="f012_name"
                                                value={localFields.f012_name || ""}
                                                onChange={handleLocalFieldChange}
                                                className="f4-input-clean"
                                            />
                                        </div>

                                        <div className="f4-code-box">013</div>
                                        <div style={{
                                            width: "100px",
                                            padding: "0 6px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontWeight: "bold",
                                            fontSize: "0.65rem",
                                            color: "var(--f4-color)",
                                            borderRight: "1px solid var(--f4-color)",
                                            lineHeight: "1.1"
                                        }}>
                                            ΟΝΟΜΑ ΠΑΤΕΡΑ
                                        </div>
                                        <div style={{
                                            width: "230px",
                                            borderRight: "1px solid var(--f4-color)",
                                            padding: "0 6px"
                                        }}>
                                            <input
                                                type="text"
                                                name="f013_fname"
                                                value={localFields.f013_fname || ""}
                                                onChange={handleLocalFieldChange}
                                                className="f4-input-clean"
                                            />
                                        </div>

                                        <div className="f4-code-box">014</div>
                                        <div style={{
                                            width: "55px",
                                            padding: "0 6px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontWeight: "bold",
                                            fontSize: "0.65rem",
                                            color: "var(--f4-color)",
                                            borderRight: "1px solid var(--f4-color)"
                                        }}>
                                            ΤΙΤΛΟΣ
                                        </div>
                                        <div style={{
                                            flexGrow: 1,
                                            padding: "0 8px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontWeight: "bold",
                                            fontSize: "0.7rem",
                                            color: "#000",
                                            overflow: "hidden"
                                        }}>
                                            {companyDetails?.title || company?.name || ""}
                                        </div>
                                    </div>
                                    <div className="f4-row" style={{height: "32px"}}>
                                        <div className="f4-code-box">015</div>
                                        <div style={{
                                            width: "175px",
                                            padding: "0 6px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontWeight: "bold",
                                            fontSize: "0.6rem",
                                            color: "var(--f4-color)",
                                            borderRight: "1px solid var(--f4-color)",
                                            lineHeight: "1.1"
                                        }}>
                                            Δ/ΝΣΗ: ΟΔΟΣ ΑΡΙΘΜΟΣ 'Η ΤΟΠΟΘΕΣΙΑ - ΧΩΡΙΟ
                                        </div>
                                        <div style={{
                                            width: "260px",
                                            padding: "0 8px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontWeight: "bold",
                                            fontSize: "0.75rem",
                                            color: "#000",
                                            borderRight: "1px solid var(--f4-color)"
                                        }}>
                                            {companyDetails?.address || ""}
                                        </div>

                                        <div className="f4-code-box">016</div>
                                        <div style={{
                                            width: "140px",
                                            padding: "0 6px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontWeight: "bold",
                                            fontSize: "0.65rem",
                                            color: "var(--f4-color)",
                                            borderRight: "1px solid var(--f4-color)"
                                        }}>
                                            ΔΗΜΟΣ 'Η ΚΟΙΝΟΤΗΤΑ
                                        </div>
                                        <div style={{
                                            width: "185px",
                                            padding: "0 8px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontWeight: "bold",
                                            fontSize: "0.75rem",
                                            color: "#000",
                                            borderRight: "1px solid var(--f4-color)"
                                        }}>
                                            {companyDetails?.city || ""}
                                        </div>

                                        <div className="f4-code-box">017</div>
                                        <div style={{
                                            flexGrow: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}>
                                            <StrictComb length={5} value={companyDetails?.postalCode || ""}
                                                        label="ΤΑΧ. ΚΩΔ"/>
                                        </div>
                                    </div>
                                    <div className="f4-row" style={{height: "36px"}}>
                                        <div style={{
                                            width: "216px",
                                            padding: "0 8px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            borderRight: "1px solid var(--f4-color)"
                                        }}>
                                            <span style={{
                                                fontWeight: "bold",
                                                fontSize: "0.65rem",
                                                color: "var(--f4-color)"
                                            }}>ΑΡΙΘΜΟΙ ΤΗΛΕΦΩΝΟΥ - FAX</span>
                                            <div style={{
                                                width: 0,
                                                height: 0,
                                                borderTop: "4px solid transparent",
                                                borderBottom: "4px solid transparent",
                                                borderLeft: "7px solid var(--f4-color)"
                                            }}></div>
                                        </div>

                                        <div className="f4-code-box">018</div>
                                        <div style={{width: "95px", borderRight: "1px solid var(--f4-color)"}}>
                                            <StrictComb
                                                length={4}
                                                value={localFields.f018_phone_code}
                                                onChange={(v) => handleLocalFieldChange({
                                                    target: {
                                                        name: "f018_phone_code",
                                                        value: v
                                                    }
                                                })}
                                                label="ΚΩΔΙΚΟΣ"
                                            />
                                        </div>
                                        <div style={{width: "235px", borderRight: "1px solid var(--f4-color)"}}>
                                            <StrictComb length={10} value={companyDetails?.phone || ""}
                                                        label="ΑΡΙΘΜΟΣ ΤΗΛΕΦΩΝΟΥ"/>
                                        </div>

                                        <div className="f4-code-box">019</div>
                                        <div style={{width: "95px", borderRight: "1px solid var(--f4-color)"}}>
                                            <StrictComb
                                                length={4}
                                                value={localFields.f019_fax_code}
                                                onChange={(v) => handleLocalFieldChange({
                                                    target: {
                                                        name: "f019_fax_code",
                                                        value: v
                                                    }
                                                })}
                                                label="ΚΩΔΙΚΟΣ"
                                            />
                                        </div>
                                        <div style={{flexGrow: 1}}>
                                            <StrictComb
                                                length={10}
                                                value={localFields.f019_fax}
                                                onChange={(v) => handleLocalFieldChange({
                                                    target: {
                                                        name: "f019_fax",
                                                        value: v
                                                    }
                                                })}
                                                label="ΑΡΙΘΜΟΣ FAX"
                                            />
                                        </div>
                                    </div>
                                    <div className="f4-row" style={{height: "55px", borderBottom: "none"}}>
                                        <div className="f4-code-box yellow" style={{fontSize: "0.85rem"}}>020</div>
                                        <div style={{
                                            width: "320px",
                                            borderRight: "1px solid var(--f4-color)",
                                            display: "flex",
                                            flexDirection: "column"
                                        }}>
                                            <div style={{
                                                fontSize: "0.6rem",
                                                fontWeight: "bold",
                                                color: "var(--f4-color)",
                                                textAlign: "center",
                                                paddingTop: "2px"
                                            }}>
                                                Α.Φ.Μ.
                                            </div>
                                            <div style={{display: "flex", flexGrow: 1}}>
                                                <div style={{
                                                    padding: "0 10px",
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    color: "var(--f4-color)",
                                                    borderRight: "1px solid var(--f4-color)",
                                                    display: "flex",
                                                    alignItems: "flex-end",
                                                    paddingBottom: "2px"
                                                }}>
                                                    E , L
                                                </div>
                                                <div style={{flexGrow: 1}}>
                                                    <StrictComb length={9} value={company?.vatNumber || ""}/>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="f4-code-box">021</div>
                                        <div style={{
                                            width: "380px",
                                            borderRight: "1px solid var(--f4-color)",
                                            display: "flex",
                                            flexDirection: "column"
                                        }}>
                                            <div style={{
                                                fontSize: "0.6rem",
                                                fontWeight: "bold",
                                                color: "var(--f4-color)",
                                                textAlign: "center",
                                                paddingTop: "2px"
                                            }}>
                                                ΑΡ. ΦΑΚΕΛΟΥ
                                            </div>
                                            <div style={{display: "flex", flexGrow: 1}}>
                                                <StrictComb
                                                    length={10}
                                                    value={localFields.f021_file}
                                                    onChange={(v) => handleLocalFieldChange({
                                                        target: {
                                                            name: "f021_file",
                                                            value: v
                                                        }
                                                    })}
                                                />
                                            </div>
                                        </div>
                                        <div style={{flexGrow: 1, display: "flex"}}>
                                            <div style={{
                                                flex: 1,
                                                borderRight: "1px solid var(--f4-color)",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "space-between",
                                                padding: "4px 0",
                                                textAlign: "center"
                                            }}>
                                                <div style={{
                                                    fontSize: "0.55rem",
                                                    fontWeight: "bold",
                                                    color: "var(--f4-color)",
                                                    lineHeight: "1.1"
                                                }}>Αριθμός<br/>σελίδας
                                                </div>
                                                <div style={{fontSize: "0.9rem", fontWeight: "bold", color: "#000"}}>1
                                                </div>
                                            </div>

                                            <div className="f4-code-box yellow" style={{
                                                width: "36px",
                                                minWidth: "36px",
                                                borderRight: "1px solid var(--f4-color)"
                                            }}>023
                                            </div>

                                            <div style={{flex: 2, display: "flex", position: "relative"}}>
                                                <div style={{
                                                    flex: 1,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    padding: "4px 0"
                                                }}>
                                                    <span style={{
                                                        fontSize: "0.6rem",
                                                        fontWeight: "bold",
                                                        color: "var(--f4-color)"
                                                    }}>α/α</span>
                                                    <span
                                                        style={{
                                                            fontSize: "0.9rem",
                                                            fontWeight: "bold",
                                                            color: "#000"
                                                        }}>1</span>
                                                </div>
                                                <svg style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%"
                                                }} preserveAspectRatio="none">
                                                    <line x1="30%" y1="100%" x2="70%" y2="0" stroke="var(--f4-color)"
                                                          strokeWidth="1.2"/>
                                                </svg>
                                                <div style={{
                                                    flex: 1,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    padding: "4px 0"
                                                }}>
                                                    <span style={{
                                                        fontSize: "0.55rem",
                                                        fontWeight: "bold",
                                                        paddingLeft: "10px",
                                                        color: "var(--f4-color)",
                                                        textAlign: "center",
                                                        lineHeight: "1.1"
                                                    }}>Σύνολο<br/>σελίδων</span>
                                                    <span
                                                        style={{
                                                            fontSize: "0.9rem",
                                                            fontWeight: "bold",
                                                            color: "#000"
                                                        }}>1</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="w-100" style={{borderBottom: "2px solid var(--f4-color)"}}>
                                    <table style={{width: "100%", borderCollapse: "collapse", textAlign: "center"}}>
                                        <thead>
                                        <tr>
                                            <th colSpan={8} style={{
                                                padding: "6px 8px",
                                                fontWeight: "bold",
                                                fontSize: "0.85rem",
                                                color: "var(--f4-color)",
                                                textAlign: "left",
                                                borderBottom: "1px solid var(--f4-color)"
                                            }}>
                                                Β. ΠΙΝΑΚΑΣ ΕΝΔΟΚΟΙΝΟΤΙΚΩΝ ΠΑΡΑΔΟΣΕΩΝ ΑΓΑΘΩΝ ΚΑΙ ΠΑΡΕΧΟΜΕΝΩΝ ΥΠΗΡΕΣΙΩΝ
                                            </th>
                                        </tr>

                                        <tr>
                                            <th rowSpan={2} style={{
                                                width: "3%",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.65rem",
                                                fontWeight: "bold"
                                            }}>
                                                α/α<br/><br/>(1)
                                            </th>
                                            <th colSpan={3} style={{
                                                width: "42%",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.7rem",
                                                fontWeight: "bold"
                                            }}>
                                                Στοιχεία αγοραστή / λήπτη υπηρεσιών
                                            </th>
                                            <th colSpan={4} style={{
                                                width: "55%",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.7rem",
                                                fontWeight: "bold"
                                            }}>
                                                Φορολογητέα Αξία**
                                            </th>
                                        </tr>

                                        <tr>
                                            <th style={{
                                                width: "15%",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.65rem",
                                                fontWeight: "bold",
                                                padding: "4px"
                                            }}>
                                                Κράτος - Μέλος<br/><br/>(2)
                                            </th>
                                            <th style={{
                                                width: "6%",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.6rem",
                                                fontWeight: "bold",
                                                padding: "4px"
                                            }}>
                                                Πρόθεμα<br/>Κ-Μ<br/>(3)
                                            </th>
                                            <th style={{
                                                width: "21%",
                                                backgroundColor: "var(--f4-box)",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.65rem",
                                                fontWeight: "bold",
                                                padding: "4px"
                                            }}>
                                                Αριθμός φορολογικού<br/>μητρώου ΦΠΑ<br/>(4)
                                            </th>
                                            <th style={{
                                                width: "14%",
                                                backgroundColor: "var(--f4-box)",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.6rem",
                                                fontWeight: "bold",
                                                padding: "4px"
                                            }}>
                                                Ενδοκοινοτικών<br/>παραδόσεων αγαθών<br/>(5)
                                            </th>
                                            <th style={{
                                                width: "14%",
                                                backgroundColor: "var(--f4-box)",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.6rem",
                                                fontWeight: "bold",
                                                padding: "4px"
                                            }}>
                                                Οιονεί ενδοκοινοτικών παραδόσεων<br/>αγαθών (τριγωνικές συναλλαγές)<br/>(6)
                                            </th>
                                            <th style={{
                                                width: "13.5%",
                                                backgroundColor: "var(--f4-box)",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.6rem",
                                                fontWeight: "bold",
                                                padding: "4px"
                                            }}>
                                                Ενδοκοινοτικών<br/>παροχών υπηρεσιών (B2B)<br/>(7)
                                            </th>
                                            <th style={{
                                                width: "13.5%",
                                                backgroundColor: "var(--f4-box)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.6rem",
                                                fontWeight: "bold",
                                                padding: "4px"
                                            }}>
                                                Ενδοκοινοτικών παραδόσεων<br/>αγαθών - καθεστώς 4200<br/>(8)
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {tableBData.map((row, idx) => {
                                            const selectedCountryObj = EU_COUNTRIES.find(c => c.label === row.ctryDesc);
                                            const countryCode = selectedCountryObj ? selectedCountryObj.code : "";

                                            return (
                                                <tr key={idx}
                                                    style={{height: "26px", borderBottom: "1px solid var(--f4-color)"}}>
                                                    <td style={{
                                                        borderRight: "1px solid var(--f4-color)",
                                                        fontWeight: "bold",
                                                        color: "var(--f4-color)",
                                                        fontSize: "0.75rem"
                                                    }}>
                                                        {idx + 1}
                                                    </td>
                                                    <td style={{borderRight: "1px solid var(--f4-color)", padding: 0}}>
                                                        <select
                                                            className="f4-select-clean font-weight-bold text-dark text-center"
                                                            style={{width: "100%", height: "25px", padding: "0 4px"}}
                                                            value={row.ctryDesc}
                                                            onChange={(e) => handleTableBChange(idx, "ctryDesc", e.target.value)}
                                                        >
                                                            <option value=""></option>
                                                            {EU_COUNTRIES.map(c => (
                                                                <option key={c.code} value={c.label}>{c.label}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td style={{borderRight: "1px solid var(--f4-color)", padding: 0}}>
                                                        <StrictComb length={2} value={countryCode}/>
                                                    </td>
                                                    <td style={{borderRight: "1px solid var(--f4-color)", padding: 0}}>
                                                        <input
                                                            type="text"
                                                            className="f4-comb-input"
                                                            style={{
                                                                padding: "0 6px",
                                                                height: "25px",
                                                                letterSpacing: "1px"
                                                            }}
                                                            maxLength={15}
                                                            value={row.bc4}
                                                            onChange={(e) => handleTableBChange(idx, "bc4", e.target.value.toUpperCase().replace(/\s/g, ""))}
                                                            placeholder=""
                                                        />
                                                    </td>
                                                    <td style={{borderRight: "1px solid var(--f4-color)", padding: 0}}>
                                                        <input
                                                            type="text"
                                                            className="f4-input-clean font-weight-bold text-dark text-right"
                                                            style={{padding: "0 6px", height: "25px"}}
                                                            value={row.bc5}
                                                            onChange={(e) => handleTableBChange(idx, "bc5", e.target.value)}
                                                        />
                                                    </td>
                                                    <td style={{
                                                        borderRight: "1px solid var(--f4-color)",
                                                        backgroundColor: "var(--f4-box)",
                                                        padding: 0
                                                    }}>
                                                        <input
                                                            type="text"
                                                            className="f4-input-clean font-weight-bold text-dark text-right"
                                                            style={{padding: "0 6px", height: "25px"}}
                                                            value={row.bc6}
                                                            onChange={(e) => handleTableBChange(idx, "bc6", e.target.value)}
                                                        />
                                                    </td>
                                                    <td style={{borderRight: "1px solid var(--f4-color)", padding: 0}}>
                                                        <input
                                                            type="text"
                                                            className="f4-input-clean font-weight-bold text-dark text-right"
                                                            style={{padding: "0 6px", height: "25px"}}
                                                            value={row.bc7}
                                                            onChange={(e) => handleTableBChange(idx, "bc7", e.target.value)}
                                                        />
                                                    </td>
                                                    <td style={{padding: 0}}>
                                                        <input
                                                            type="text"
                                                            className="f4-input-clean font-weight-bold text-dark text-right"
                                                            style={{padding: "0 6px", height: "25px"}}
                                                            value={row.bc8}
                                                            onChange={(e) => handleTableBChange(idx, "bc8", e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        <tr style={{height: "30px"}}>
                                            <td colSpan={4} style={{
                                                borderRight: "1px solid var(--f4-color)",
                                                textAlign: "center",
                                                fontWeight: "bold",
                                                color: "var(--f4-color)",
                                                fontSize: "0.8rem",
                                                letterSpacing: "1px"
                                            }}>
                                                ΣΥΝΟΛΑ ΣΕΛΙΔΑΣ
                                            </td>
                                            <td style={{
                                                borderRight: "1px solid var(--f4-color)",
                                                textAlign: "right",
                                                paddingRight: "6px",
                                                fontWeight: "bold",
                                                fontSize: "0.75rem",
                                                color: "#000"
                                            }}>
                                                {getGreekCurrencyFormat(sumBc5)}
                                            </td>
                                            <td style={{
                                                borderRight: "1px solid var(--f4-color)",
                                                backgroundColor: "var(--f4-box)",
                                                textAlign: "right",
                                                paddingRight: "6px",
                                                fontWeight: "bold",
                                                fontSize: "0.75rem",
                                                color: "#000"
                                            }}>
                                                {getGreekCurrencyFormat(sumBc6)}
                                            </td>
                                            <td style={{
                                                borderRight: "1px solid var(--f4-color)",
                                                textAlign: "right",
                                                paddingRight: "6px",
                                                fontWeight: "bold",
                                                fontSize: "0.75rem",
                                                color: "#000"
                                            }}>
                                                {getGreekCurrencyFormat(sumBc7)}
                                            </td>
                                            <td style={{
                                                textAlign: "right",
                                                paddingRight: "6px",
                                                fontWeight: "bold",
                                                fontSize: "0.75rem",
                                                color: "#000"
                                            }}>
                                                {getGreekCurrencyFormat(sumBc8)}
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className="w-100 pb-2">
                                    <table style={{
                                        width: "96%",
                                        margin: "0 auto",
                                        borderCollapse: "collapse",
                                        textAlign: "center",
                                        border: "2px solid var(--f4-color)"
                                    }}>
                                        <thead>
                                        <tr>
                                            <th colSpan={7} style={{
                                                padding: "6px 8px",
                                                fontWeight: "bold",
                                                fontSize: "0.85rem",
                                                color: "var(--f4-color)",
                                                textAlign: "left",
                                                borderBottom: "1px solid var(--f4-color)"
                                            }}>
                                                Γ. ΠΙΝΑΚΑΣ ΓΙΑ ΤΑ ΑΠΟΘΕΜΑΤΑ ΣΤΗ ΔΙΑΘΕΣΗ ΣΥΓΚΕΚΡΙΜΕΝΟΥ ΠΕΛΑΤΗ ΣΕ ΑΛΛΟ Κ-Μ
                                            </th>
                                        </tr>

                                        <tr style={{height: "70px"}}>
                                            <th style={{
                                                width: "4%",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.65rem",
                                                fontWeight: "bold"
                                            }}>
                                                α/α<br/><br/>(1)
                                            </th>
                                            <th style={{
                                                width: "16%",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.7rem",
                                                fontWeight: "bold"
                                            }}>
                                                Κράτος - Μέλος<br/><br/>(2)
                                            </th>
                                            <th style={{
                                                width: "7%",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.6rem",
                                                fontWeight: "bold"
                                            }}>
                                                Πρόθεμα<br/>Κ-Μ<br/>(3)
                                            </th>
                                            <th style={{
                                                width: "29%",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.65rem",
                                                fontWeight: "bold",
                                                padding: "4px"
                                            }}>
                                                Αριθμός φορολογικού<br/>μητρώου<br/>ΦΠΑ συγκεκριμένου<br/>πελάτη<br/>(4)
                                            </th>
                                            <th style={{
                                                width: "30%",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.65rem",
                                                fontWeight: "bold",
                                                padding: "4px"
                                            }}>
                                                Αριθμός φορολογικού<br/>μητρώου<br/>ΦΠΑ νέου συγκεκριμένου<br/>πελάτη σε
                                                αντικατάσταση<br/>προηγούμενου<br/>(5)
                                            </th>
                                            <th style={{
                                                width: "7%",
                                                borderRight: "1px solid var(--f4-color)",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.6rem",
                                                fontWeight: "bold",
                                                padding: 0
                                            }}>
                                                <div className="vertical-text">ΕΠΙΣΤΡΟΦΗ<br/>ΑΓΑΘΩΝ</div>
                                                (6)
                                            </th>
                                            <th style={{
                                                width: "7%",
                                                borderBottom: "1px solid var(--f4-color)",
                                                color: "var(--f4-color)",
                                                fontSize: "0.6rem",
                                                fontWeight: "bold",
                                                padding: 0
                                            }}>
                                                <div className="vertical-text"
                                                     style={{paddingBottom: "10px"}}>ΔΙΟΡΘΩΣΗ
                                                </div>
                                                (7)
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {tableCData.map((row, idx) => {
                                            const selectedCountryObj = EU_COUNTRIES.find(c => c.label === row.ctryDesc);
                                            const countryCode = selectedCountryObj ? selectedCountryObj.code : "";

                                            return (
                                                <tr key={idx}
                                                    style={{height: "26px", borderBottom: "1px solid var(--f4-color)"}}>
                                                    <td style={{
                                                        borderRight: "1px solid var(--f4-color)",
                                                        fontWeight: "bold",
                                                        color: "var(--f4-color)",
                                                        fontSize: "0.8rem"
                                                    }}>
                                                        {idx + 1}
                                                    </td>
                                                    <td style={{borderRight: "1px solid var(--f4-color)", padding: 0}}>
                                                        <select
                                                            className="f4-select-clean font-weight-bold text-dark text-center"
                                                            style={{width: "100%", height: "25px", padding: "0 4px"}}
                                                            value={row.ctryDesc}
                                                            onChange={(e) => handleTableCChange(idx, "ctryDesc", e.target.value)}
                                                        >
                                                            <option value=""></option>
                                                            {EU_COUNTRIES.map(c => (
                                                                <option key={c.code} value={c.label}>{c.label}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td style={{borderRight: "1px solid var(--f4-color)", padding: 0}}>
                                                        <StrictComb length={2} value={countryCode}/>
                                                    </td>
                                                    <td style={{borderRight: "1px solid var(--f4-color)", padding: 0}}>
                                                        <input
                                                            type="text"
                                                            className="f4-comb-input"
                                                            style={{
                                                                padding: "0 6px",
                                                                height: "25px",
                                                                letterSpacing: "1px"
                                                            }}
                                                            maxLength={15}
                                                            value={row.cc4}
                                                            onChange={(e) => handleTableCChange(idx, "cc4", e.target.value.toUpperCase().replace(/\s/g, ""))}
                                                        />
                                                    </td>
                                                    <td style={{borderRight: "1px solid var(--f4-color)", padding: 0}}>
                                                        <input
                                                            type="text"
                                                            className="f4-comb-input"
                                                            style={{
                                                                padding: "0 6px",
                                                                height: "25px",
                                                                letterSpacing: "1px"
                                                            }}
                                                            maxLength={15}
                                                            value={row.cc5}
                                                            onChange={(e) => handleTableCChange(idx, "cc5", e.target.value.toUpperCase().replace(/\s/g, ""))}
                                                        />
                                                    </td>
                                                    <td style={{borderRight: "1px solid var(--f4-color)", padding: 0}}>
                                                        <select
                                                            className="f4-select-clean font-weight-bold text-dark text-center"
                                                            style={{width: "100%", height: "25px"}}
                                                            value={row.cc6}
                                                            onChange={(e) => handleTableCChange(idx, "cc6", e.target.value)}
                                                        >
                                                            <option value="0"></option>
                                                            <option value="1">X</option>
                                                        </select>
                                                    </td>
                                                    <td style={{padding: 0}}>
                                                        <select
                                                            className="f4-select-clean font-weight-bold text-dark text-center"
                                                            style={{width: "100%", height: "25px"}}
                                                            value={row.cc7}
                                                            onChange={(e) => handleTableCChange(idx, "cc7", e.target.value)}
                                                        >
                                                            <option value="0"></option>
                                                            <option value="1">X</option>
                                                        </select>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="d-flex justify-content-between align-items-center px-1 mt-1 mb-1"
                                     style={{fontSize: "0.5rem"}}>
                                    <div className="d-flex align-items-center">
                                        <div className="vertical-text mr-2 font-weight-bold"
                                             style={{height: "40px", fontSize: "0.45rem", color: "var(--f4-color)"}}>
                                            Γ.Γ.Π.Σ. - ΔΛΟΣΕΛΥ
                                        </div>
                                        <div className="d-flex flex-column" style={{lineHeight: 1.2}}>
                                            <span style={{color: "var(--f4-color)"}}>Συμπληρώνεται από τη ΔΟΥ</span>
                                            <span style={{color: "var(--f4-color)"}}>** Τα ποσά αναγράφονται ΥΠΟΧΡΕΩΤΙΚΑ ΣΕ ΕΥΡΩ με τη χρήση δύο (2) δεκαδικών ψηφίων μετά την υποδιαστολή, π.χ. 45,00 ή 48,65.</span>
                                        </div>
                                    </div>
                                    <span className="font-weight-bold" style={{fontSize: "0.6rem"}}>ΕΚΔΟΣΗ 2020</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className="w-100 position-sticky bg-white border-top py-1 px-2 d-flex justify-content-between align-items-center print-hide mt-auto"
                        style={{
                            bottom: 0,
                            zIndex: 3,
                            boxShadow: "0 -4px 10px -2px rgba(0,0,0,0.1)"
                        }}
                    >
                        <div className="d-flex align-items-center px-1 py-1 mx-1">
                            <span className="mr-2 text-dark font-weight-bold"
                                  style={{fontSize: "0.7rem", fontWeight: "600"}}>
                                Zoom:
                            </span>
                            <Button
                                variant="light"
                                size="sm"
                                className="py-1 px-2 border font-weight-bold"
                                onClick={() => dispatch(setF4ZoomLevel(Math.max(Number((zoomLevel - 0.1).toFixed(1)), 0.4)))}
                            >
                                -
                            </Button>
                            <span className="mx-2 font-weight-bold text-primary"
                                  style={{minWidth: "35px", textAlign: "center", fontSize: "0.75rem"}}>
                                {Math.round(zoomLevel * 100)}%
                            </span>
                            <Button
                                variant="light"
                                size="sm"
                                className="py-1 px-2 border font-weight-bold"
                                onClick={() => dispatch(setF4ZoomLevel(Math.min(Number((zoomLevel + 0.1).toFixed(1)), 2.0)))}
                            >
                                +
                            </Button>
                            {zoomLevel !== 1 && (
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="py-1 px-1 ml-2 border"
                                    onClick={() => dispatch(setF4ZoomLevel(1))}
                                    title="Επαναφορά στο 100%"
                                >
                                    <i className="fa-solid fa-arrow-rotate-left"></i>
                                </Button>
                            )}
                        </div>

                        <div className="d-flex align-items-center">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleTriggerExtension}
                                disabled={taxisLoading}
                            >
                                {taxisLoading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="mr-2"/>
                                        Σύνδεση με Taxis...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-cloud-arrow-up fa-sm mr-2"></i>
                                        Συμπλήρωση στο Taxis
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </>
            )}

            <Modal
                show={showExtensionModal}
                onHide={() => setShowExtensionModal(false)}
                dialogClassName={"modal65PercentWidth"}
                centered
            >
                <Modal.Header closeButton className="border-bottom-0 pb-2">
                    <Modal.Title className="d-flex align-items-center">
                        <div>
                            <h5 className="mb-0 font-weight-bold" style={{fontSize: "1.05rem", color: "#111827"}}>
                                Απαιτείται το Πρόσθετο Primer ERP - Taxis Bridge
                            </h5>
                            <span className="text-muted" style={{fontSize: "0.75rem"}}>
                                Αυτοματοποίηση διασύνδεσης με το σύστημα Taxisnet
                            </span>
                        </div>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="pt-2 pb-3 px-4">
                    <p className="text-muted mb-3" style={{fontSize: "0.85rem", lineHeight: "1.45"}}>
                        Για την ασφαλή άντληση των δηλώσεων ΦΠΑ και την αυτόματη επικοινωνία με την ΑΑΔΕ, είναι
                        απαραίτητη η εγκατάσταση και σωστή ρύθμιση του εργαλείου στον περιηγητή σας.
                    </p>

                    <div
                        className="p-3 mb-3 rounded"
                        style={{
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0"
                        }}
                    >
                        <div className="font-weight-bold mb-3 text-dark" style={{fontSize: "0.8rem"}}>
                            Οδηγίες Εγκατάστασης:
                        </div>

                        <div className="d-flex flex-column" style={{gap: "10px"}}>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    1
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Πατήστε το κουμπί <strong>«Μετάβαση στο Chrome Web Store»</strong> παρακάτω.
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    2
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Στη σελίδα που θα ανοίξει, πατήστε <strong>«Προσθήκη στο Chrome»</strong> (Add to Chrome).
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    3
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Ανοίξτε τη διαχείριση επεκτάσεων (πληκτρολογήστε <code
                                    className="px-1 py-0.5 bg-light rounded border text-danger">chrome://extensions</code> στη γραμμή διευθύνσεων ή πατήστε το εικονίδιο παζλ πάνω δεξιά).
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    4
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Εντοπίστε το <strong>Primer ERP - Taxis Bridge</strong> και πατήστε <strong>Λεπτομέρειες (Details)</strong>.
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    5
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Ενεργοποιήστε την επιλογή <strong>«Να επιτρέπεται σε ανώνυμη περιήγηση» (Allow in incognito)</strong>.
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center text-muted" style={{fontSize: "0.75rem"}}>
                        <i className="fa-solid fa-shield-halved text-primary mr-2"></i>
                        <span>Ασφαλές εργαλείο, εγκεκριμένο από το επίσημο Chrome Web Store.</span>
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-top-0 pt-0 px-4 pb-3">
                    <a
                        href={EXTENSION_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm px-3 d-inline-flex align-items-center"
                        onClick={() => setShowExtensionModal(false)}
                    >
                        <i className="fa-brands fa-chrome mr-2"></i>
                        Μετάβαση στο Chrome Web Store
                    </a>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AccountingIssueF4VatInformation;